import { useMemo } from 'react';

import { Groups } from '@mui/icons-material';
import {
  Avatar,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { GridFilterInputValueProps } from '@mui/x-data-grid';

import AutocompleteComponent from '@/components/autocomplete';
import { useFetchCreatorsAutocomplete } from '@/hooks/queries/creators';
import { CreatorsAutocomplete, CreatorGroups } from '@/models/creator';
import nFormatter from '@/utils/nFormatter';

export type AutocompleteOption =
  | CreatorsAutocomplete.Creator
  | CreatorGroups.Group;

interface CreatorFilterProps extends GridFilterInputValueProps {
  showGroups?: boolean;
  violations?: boolean;
  evaluationId?: number;
}

const FilterCreators = ({
  item,
  applyValue,
  showGroups = true,
  violations = false,
  evaluationId
}: CreatorFilterProps) => {
  const { data: options = { creators: [], creatorGroups: [] }, isLoading } =
    useFetchCreatorsAutocomplete({
      params: { groups: showGroups, creators: true, violations, evaluationId }
    });

  const allOptions = useMemo(
    () => [...options.creators, ...options.creatorGroups],
    [options]
  );

  const getSelectedValue = () => {
    if (!item.value) return [];
    if (Array.isArray(item.value) && typeof item.value[0] === 'object') {
      return item.value;
    }
    if (typeof item.value === 'string') {
      const selectedIds = new Set(item.value.split(','));
      return allOptions.filter((option) => selectedIds.has(String(option.id)));
    }
    return [];
  };

  const handleChange = (_: any, newValue: AutocompleteOption[]) => {
    applyValue({ ...item, value: newValue });
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 48
        }}
      >
        <CircularProgress size={20} color="inherit" />
      </div>
    );
  }

  return (
    <AutocompleteComponent
      value={getSelectedValue()}
      multiple
      loading={isLoading}
      options={allOptions}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) =>
        option.type === value.type && option.id === value.id
      }
      groupBy={
        showGroups &&
        options.creators.length > 0 &&
        options.creatorGroups.length > 0
          ? (option: AutocompleteOption) => option.type
          : undefined
      }
      getOptionLabel={(option: AutocompleteOption) => option.name}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          size="small"
          label="Creators"
          sx={{
            minWidth: 350,
            pr: '10px',
            mt: 0.3
          }}
        />
      )}
      renderOption={(htmlAttributes, option) => {
        const { key, ...restHtmlAttributes } =
          htmlAttributes as React.HTMLAttributes<HTMLElement> & {
            key?: string;
          };

        if (
          violations &&
          'violationsCount' in option &&
          typeof option.violationsCount === 'number' &&
          option.violationsCount <= 0
        ) {
          return null;
        }

        return (
          <Stack
            key={key}
            {...restHtmlAttributes}
            direction="row"
            alignItems="center"
            style={{ margin: '5px 0px' }}
          >
            <Typography>{option.name}</Typography>
            {'totalItems' in option && option.type === 'Groups' && (
              <Typography color="textSecondary" ml="auto">
                {nFormatter(option.totalItems)}
              </Typography>
            )}
            {violations &&
              'violationsCount' in option &&
              option.type === 'Creators' && (
                <Typography color="textSecondary" ml="auto">
                  {nFormatter(option.violationsCount)}
                </Typography>
              )}
          </Stack>
        );
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const color = tag.type === 'Creators' ? 'primary' : 'secondary';
          if ('avatarUrl' in tag && tag.type === 'Creators' && tag.avatarUrl) {
            return (
              <Chip
                {...getTagProps({ index })}
                color={color}
                avatar={<Avatar alt={tag.name} src={tag.avatarUrl} />}
                key={`chip__${tag.name}`}
                label={tag.name}
                size="small"
              />
            );
          }
          return (
            <Chip
              {...getTagProps({ index })}
              color={color}
              avatar={
                tag.type === 'Creators' ? (
                  <Avatar>{tag.name.split('')[0]}</Avatar>
                ) : undefined
              }
              icon={
                tag.type === 'Groups' ? (
                  <Groups style={{ marginLeft: 6 }} />
                ) : undefined
              }
              key={`chip__${tag.name}`}
              label={tag.name}
              size="small"
            />
          );
        })
      }
    />
  );
};

export default FilterCreators;
